import { ReactNode, useCallback, useState } from 'react';
import { useProductName } from '../lib/use-brand';
import { useTranslation } from '../lib/i18n';
import Link from 'next/link';
import { chatHandoffLink } from '../lib/chat-handoff';
import { classifyDashboardErrors, toolkitDisplayName } from '../lib/dashboard-error-kind';
import { connectorsPath } from '../lib/connectors-route';

// The error tint differs per host surface (Orion's PageSurface renders on a darker app chrome, Symphony's
// DashboardFrame on a light one), so the caller passes the three colours; everything else — the
// aggregation, the bundled copy / hand-off, and the (non-overlapping) layout — is shared.
export interface ErrorBannerPalette {
  color: string;
  background: string;
  border: string;
}

/**
 * The dashboard's self-heal error stripe. It's a FLOW element (not absolute) so it pushes the dashboard
 * down instead of overlapping it. Orion is a consumer product, not a dev console, so the raw error text is
 * DELIBERATELY not shown — the stripe reads as a calm one-line notice. The host's full, deduped error list
 * (a failed widget `sym.call` or an uncaught runtime error) rides only in the Copy payload and the "Ask
 * Orion to fix" hand-off, so a single tap reports them all together. The host owns the list (dedup +
 * ordering); this only summarises + bundles it.
 *
 * WHAT IT MUST NOT DO IS BLAME US FOR SOMEONE ELSE'S PROBLEM, and most of what lands here is someone
 * else's. `classifyDashboardErrors` splits the collected lines into four, and this renders ONE STRIPE PER
 * GROUP PRESENT — not one per failing widget, and not one shared sentence for causes that call for
 * opposite actions:
 *
 * - **Not connected** — the app has no account on this metasite. One stripe naming the app and linking to
 *   its row on the Connectors page. THREE widgets waiting on Gmail collapse to ONE line, because it is one
 *   fact and one click; listing it per widget tripled the alarm and hid that a single action cleared all of
 *   them. No Copy, no hand-off: the assistant cannot connect an account, the user can.
 * - **Rate limited** — the source refused the read for volume. One stripe saying it clears on its own, with
 *   a retry (cheap: it re-fetches the same dashboard). Never presented as something to fix.
 * - **Outage** — a connected service is not answering. Names the service, offers retry.
 * - **Defect** — the old treatment, unchanged: "didn't load correctly", Copy details, "Ask Orion to fix".
 *
 * A page can be in several of these at once, and a genuine defect ALWAYS still gets its stripe — the new
 * classes narrow what counts as a defect, they never swallow one.
 *
 * ⚠️ NO STRIPE EVER RENDERS A RAW LINE. The collected strings are the runtime's diagnostics —
 * `<widget-slug>: <provider JSON>`, carrying request ids, an internal entity id, and a blob the shim
 * truncates mid-token. That reached a user's Home page and is what this change exists to stop. The raw text
 * still travels, unchanged and complete, in exactly two places: the "Copy details" payload and the "Ask
 * Orion to fix" hand-off, where the reader is a human debugging or the assistant repairing.
 */
export function DashboardErrorBanner({
  basePath,
  title,
  errors,
  palette,
  onRetry,
}: {
  basePath: string;
  title: string;
  errors: string[];
  palette: ErrorBannerPalette;
  // Re-fetch this dashboard. Falls back to a full reload when the host has nothing finer to offer.
  onRetry?: () => void;
}) {
  const { defects, outage, services, disconnectedToolkits, rateLimited, all } = classifyDashboardErrors(errors);
  if (!all.length) return null;
  return (
    <>
      {disconnectedToolkits.length > 0 && (
        <NotConnectedNotice basePath={basePath} toolkits={disconnectedToolkits} palette={palette} />
      )}
      {rateLimited && <RateLimitedNotice palette={palette} onRetry={onRetry} />}
      {/* `outage`, NOT `services.length` — an outage whose endpoint we could not read names nothing, and
          keying the stripe off the names would render no banner at all for it. */}
      {outage && <OutageNotice services={services} palette={palette} onRetry={onRetry} />}
      {defects.length > 0 && (
        <DefectNotice basePath={basePath} title={title} errors={defects} allErrors={all} palette={palette} />
      )}
    </>
  );
}

// Nothing failed and nothing is broken: an app these cards read from was never connected. The one class
// here with a cure the USER can apply, so it is the one class that gets a route instead of a hand-off —
// the assistant cannot complete someone's OAuth for them. One line per APP, not per waiting widget.
function NotConnectedNotice({ basePath, toolkits, palette }: { basePath: string; toolkits: string[]; palette: ErrorBannerPalette }) {
  const { t } = useTranslation();
  const names = toolkits.map(toolkitDisplayName).filter(Boolean);
  const heading = names.length ? t('dashboardError.notConnected', { service: names.join(', ') }) : t('dashboardError.notConnectedGeneric');
  // The Connectors page's Tools tab — the SAME route every other connect affordance uses
  // (`ConnectToolsCard`, `ConnectorConnectCard`, `Home2ConnectorsSection`), not a new one.
  //
  // Deliberately NOT `{ tab: 'tools', target: toolkit }`. `parseConnectorsRoute` accepts that happily and
  // `connectorsPath` builds it, but only the CHANNELS tab renders `connectorsTargetElementId` anchors —
  // the tools rows have no ids to scroll to. The link would resolve, land on the tab, and silently
  // highlight nothing, which is exactly the failure `connectors-route.ts` warns about. If the tools rows
  // ever grow anchors, aim this at the row.
  const href = `${basePath}${connectorsPath({ tab: 'tools' })}`;
  return (
    <Stripe palette={palette}>
      <span style={{ flex: 1, minWidth: 0 }}>{heading}</span>
      <Link href={href} prefetch={false} style={{ color: 'rgba(var(--ink-rgb),0.96)', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
        {t('common.connect')}
      </Link>
    </Stripe>
  );
}

// Refused for volume. It clears with time on its own, so this states that plainly and offers the one cheap
// action — ask again. Deliberately NOT worded as a problem to fix: there is nothing here to repair.
function RateLimitedNotice({ palette, onRetry }: { palette: ErrorBannerPalette; onRetry?: () => void }) {
  const { t } = useTranslation();
  const retry = useCallback(() => {
    if (onRetry) onRetry();
    else if (typeof window !== 'undefined') window.location.reload();
  }, [onRetry]);
  return (
    <Stripe palette={palette}>
      <span style={{ flex: 1, minWidth: 0 }}>{t('dashboardError.rateLimited')}</span>
      <StripeAction onClick={retry} label={t('common.retry')} />
    </Stripe>
  );
}

// Nothing on our side failed: a connected service is not answering. The cards already show their own
// designed "couldn't reach X" state, so this only names who is down and offers the one action that can
// help — asking again.
function OutageNotice({ services, palette, onRetry }: { services: string[]; palette: ErrorBannerPalette; onRetry?: () => void }) {
  const { t } = useTranslation();
  const heading = services.length ? t('dashboardError.outage.heading', { service: services.join(', ') }) : t('dashboardError.outage.headingGeneric');
  const retry = useCallback(() => {
    if (onRetry) onRetry();
    else if (typeof window !== 'undefined') window.location.reload();
  }, [onRetry]);
  return (
    <Stripe palette={palette}>
      <span style={{ flex: 1, minWidth: 0 }}>{heading}</span>
      <StripeAction onClick={retry} label={t('common.retry')} />
    </Stripe>
  );
}

function DefectNotice({
  basePath,
  title,
  errors,
  allErrors,
  palette,
}: {
  basePath: string;
  title: string;
  /** The DEFECT lines — what the assistant is asked to repair. */
  errors: string[];
  /** Every collected line, defects and non-defects alike — what a human debugging wants. */
  allErrors: string[];
  palette: ErrorBannerPalette;
}) {
  const count = errors.length;
  const productName = useProductName();
  const { t } = useTranslation();
  // What the USER sees — a friendly, non-technical summary. No count, no error strings. (The real errors are
  // in the Copy payload / hand-off below.)
  const heading = count === 1 ? t('dashboardError.heading.one') : t('dashboardError.heading.other');

  // TWO payloads, deliberately different, because they have different readers.
  //
  // COPY DETAILS is read by a HUMAN — the user pasting into a bug report, or an engineer. It carries EVERY
  // collected line verbatim, non-defects included: the not-connected and rate-limited bodies are the whole
  // evidence for why those stripes appeared, and dropping them would leave a report that cannot be checked.
  const copyBundle = allErrors.length === 1 ? allErrors[0] : allErrors.map((e) => `- ${e}`).join('\n');
  const copyCount = allErrors.length;
  const errorText =
    copyCount === 1
      ? t('dashboardError.copyText.one', { title, bundle: copyBundle })
      : t('dashboardError.copyText.other', { title, errorCount: copyCount, bundle: copyBundle });

  // THE HAND-OFF is read by the ASSISTANT, which is being asked to repair the page. It carries ONLY the
  // defect lines — the long-standing rule, and it extends to the new classes for the same reason: the
  // assistant cannot connect a user's Gmail account or lift someone else's rate limit, so handing it those
  // lines sends it off to "fix" widgets that are not broken.
  const bundle = count === 1 ? errors[0] : errors.map((e) => `- ${e}`).join('\n');
  const handoffMessage =
    count === 1 ? t('dashboardError.handoff.one', { title, bundle }) : t('dashboardError.handoff.other', { title, errorCount: count, bundle });
  const link = chatHandoffLink({ kind: 'new', message: handoffMessage }, basePath);

  const [copied, setCopied] = useState(false);
  const copyError = useCallback(() => {
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    };
    const write = navigator.clipboard?.writeText(errorText);
    if (write) {
      void write.then(done, () => undefined);
    } else {
      done();
    }
  }, [errorText]);

  return (
    <Stripe palette={palette}>
      {/* Only the friendly summary is shown. The raw errors are intentionally kept off-screen — they travel
          in the Copy payload and the "Ask Orion to fix" hand-off, not in front of the user. */}
      <span style={{ flex: 1, minWidth: 0 }}>{heading}</span>
      <StripeAction onClick={copyError} label={copied ? t('dashboardError.copied') : t('dashboardError.copy')} />
      <Link
        href={link.route}
        as={link.path}
        prefetch={false}
        style={{ color: 'rgba(var(--ink-rgb),0.96)', textDecoration: 'underline', whiteSpace: 'nowrap' }}
      >
        {t('dashboardError.askToFix', { productName })}
      </Link>
    </Stripe>
  );
}

function Stripe({ palette, children }: { palette: ErrorBannerPalette; children: ReactNode }) {
  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
        padding: '10px 14px',
        fontSize: 13,
        lineHeight: 1.5,
        color: palette.color,
        background: palette.background,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
      }}
    >
      {children}
    </div>
  );
}

// The stripe's inline text action. One implementation for both notices, so the stripe keeps a single
// hand-rolled control (see test/button-spec.test.ts's pending-migration ratchet).
function StripeAction({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 'none',
        background: 'transparent',
        color: 'inherit',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 13,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}
